import { useState } from 'react'
import { DetailModal } from '@/components/dashboard/detail-modal'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
} from '@/components/shared/types'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { OrderItemGridCard } from './order-item-card/order-item-grid-card'
import { OrderItemGridCardSkeleton } from './order-item-card/order-item-grid-card-skeleton'

type Props = {
  orderItems: OrderItem[]
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  isFetching: boolean
  currentPage: number
  pageSize: number
  lastItemRef: (node: HTMLDivElement | null) => void
  toggleRow: (orderItemId: number) => void
  isLoading?: boolean
} & CreateCampaignCallbacks

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OrderItemGrid: React.FC<Props> = props => {
  const {
    orderItems,
    onDuplicateCampaign,
    onArchiveCampaign,
    isFetching,
    currentPage,
    pageSize,
    lastItemRef,
    toggleRow,
    isLoading = false,
  } = props
  const [selectedOrderItem, setSelectedOrderItem] = useState<OrderItem | null>(null)

  const showInitialLoading = isFetching && currentPage === 0
  const showNextPageLoading = isFetching && currentPage > 0

  return (
    <>
      <div className="w-full flex flex-wrap justify-start [&>*]:max-w-sm">
        {showInitialLoading && <LoadingOrderItemGridCards numRows={pageSize} keyPrefix="loading" />}
        {orderItems.map((orderItem, index) => {
          return (
            <OrderItemGridCard
              key={orderItem.orderItemId}
              orderItem={orderItem}
              isVisible={!showInitialLoading}
              isDisabled={isLoading}
              animationDelay={index * 50}
              handleSelectOrderItem={() => setSelectedOrderItem(orderItem)}
              handleToggleRow={() => toggleRow(orderItem.orderItemId)}
              onCreateCampaignV2={props.onCreateCampaignV2}
            />
          )
        })}
        {showNextPageLoading && <LoadingOrderItemGridCards numRows={pageSize} keyPrefix="fetching" />}
        {selectedOrderItem && (
          <DetailModal
            isLoading={isLoading}
            selectedOrderItem={selectedOrderItem}
            onClose={() => setSelectedOrderItem(null)}
            onCreateCampaignV2={props.onCreateCampaignV2}
            onDuplicateCampaign={onDuplicateCampaign}
            onArchiveCampaign={onArchiveCampaign}
            open
          />
        )}
      </div>
      <div ref={lastItemRef} />
    </>
  )
}

interface LoadingOrderItemGridCardsProps {
  numRows: number
  keyPrefix: string
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const LoadingOrderItemGridCards: React.FC<LoadingOrderItemGridCardsProps> = ({ numRows, keyPrefix }) => {
  return (
    <>
      {Array.from({ length: numRows }).map((_, index) => (
        <OrderItemGridCardSkeleton key={`${keyPrefix}-${index}`} />
      ))}
    </>
  )
}
